// PythonExplainerDocStyled.tsx
import * as React from "react";
import { Box, Stack, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";

type Row =
  | { kind: "code"; text: string }
  | { kind: "comment"; text: string }
  | { kind: "docTitle"; text: string }
  | { kind: "docBody"; text: string };

type Props = {
  code: string;
  preserveEmptyLines?: boolean;
};

const CodeBlock = styled(Box)(({ theme }) => ({
  fontFamily:
    'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
  whiteSpace: "pre",
  background: theme.palette.grey[900],
  color: theme.palette.grey[200],
  borderRadius: 4,
  padding: 10,
  overflowX: "auto",
  margin: 0,
})).withComponent("pre");

const CommentBlock = styled(Box)(({ theme }) => ({
  whiteSpace: "pre-wrap",
  fontSize: "small",
  paddingTop: 10,
  paddingBottom: 10,
}));

const DocTitle = styled(Typography)(({ theme }) => ({
  color: theme.palette.blues.rothko3,
  fontWeight: "bolder",
  fontSize: "large",
}));

const DocBody = styled(Typography)(({ theme }) => ({
  color: theme.palette.blues.rothko2,
  whiteSpace: "pre-wrap",
  fontSize: "medium",
  fontWeight: "normal",
  paddingTop: 10,
  paddingBottom: 10,
}));

type DocQuote = '"""' | "'''";
const isCommentOnly = (line: string) => /^\s*#/.test(line);
const docOpen = (line: string): DocQuote | null => {
  const m = line.match(/^\s*("""|''')/);
  const q = m?.[1];
  return q === '"""' || q === "'''" ? q : null;
};
const stripOpen = (line: string, q: DocQuote) =>
  line.replace(new RegExp(`^\\s*${q}`), "");
const stripClose = (line: string, q: DocQuote) =>
  line.replace(new RegExp(`${q}\\s*$`), "");

/** Parse a collected docstring block into rows with optional Title/Body styling */
function parseDocstringBlock(blockLines: string[]): Row[] {
  while (blockLines.length && blockLines[0].trim() === "") blockLines.shift();
  while (blockLines.length && blockLines[blockLines.length - 1].trim() === "")
    blockLines.pop();

  if (blockLines.length === 0) return [];

  // Title
  // ======
  const [maybeTitle, maybeUnderline, ...rest] = blockLines;
  const underlineIsRule =
    typeof maybeUnderline === "string" &&
    /^([=\-])\1+$/.test(maybeUnderline.trim()) &&
    maybeUnderline.trim().length >= maybeTitle.trim().length;

  if (underlineIsRule) {
    const body = rest.join(" ");
    const rows: Row[] = [{ kind: "docTitle", text: maybeTitle.trim() }];
    if (body.trim().length) rows.push({ kind: "docBody", text: body });
    return rows;
  }

  return [{ kind: "docBody", text: blockLines.join("\n") }];
}

export default function PythonExplainerDocStyled({
  code,
  preserveEmptyLines = true,
}: Props) {
  const lines = React.useMemo(
    () => code.replace(/\r\n?/g, "\n").split("\n"),
    [code]
  );

  const rows = React.useMemo(() => {
    const out: Row[] = [];
    let inDoc = false;
    let quote: DocQuote | null = null;
    let docBuf: string[] = [];

    let commentBuf: string[] = [];
    const flushComment = () => {
      if (commentBuf.length) {
        // Strip one leading '# ' from each line (already done when pushing),
        // just join as one block.
        out.push({ kind: "comment", text: commentBuf.join(" ") });
      }
      commentBuf = [];
    };

    // existing code buffer
    let codeBuf: string[] = [];
    const flushCode = () => {
      if (codeBuf.length && codeBuf.some((l) => l.length > 0)) {
        out.push({ kind: "code", text: codeBuf.join("\n") });
      }
      codeBuf = [];
    };

    const flushDoc = () => {
      if (docBuf.length) out.push(...parseDocstringBlock(docBuf));
      docBuf = [];
    };

    for (let raw of lines) {
      // Handle blank lines: keep them INSIDE the active buffer
      if (!raw.length && preserveEmptyLines && !inDoc) {
        if (commentBuf.length) {
          commentBuf.push(""); // blank in comment run
        } else {
          codeBuf.push(""); // blank in code run
        }
        continue;
      }

      if (inDoc && quote) {
        // closing line?
        if (raw.includes(quote)) {
          const before = stripClose(raw, quote);
          if (before.length) docBuf.push(before);
          flushDoc();
          inDoc = false;
          quote = null;
        } else {
          docBuf.push(raw);
        }
        continue;
      }

      // comment-only line
      if (isCommentOnly(raw)) {
        flushCode();
        // push stripped (remove one leading "# ")
        commentBuf.push(raw.replace(/^\s*#\s?/, ""));
        continue;
      }

      // docstring open?
      const q = docOpen(raw);
      if (q) {
        // entering docstring → flush both code and comment buffers
        flushComment();
        flushCode();
        const afterOpen = stripOpen(raw, q);
        if (afterOpen.includes(q)) {
          const inner = stripClose(afterOpen, q).trimEnd();
          out.push(...parseDocstringBlock(inner.split("\n")));
        } else {
          inDoc = true;
          quote = q;
          if (afterOpen.trimEnd().length) docBuf.push(afterOpen.trimEnd());
        }
        continue;
      }

      // normal code line
      // if we were in a comment run, close it before starting/continuing code
      if (commentBuf.length) flushComment();
      codeBuf.push(raw);
    }

    // flush trailing buffers
    if (inDoc) flushDoc();
    flushComment();
    flushCode();

    return out;
  }, [lines, preserveEmptyLines]);

  return (
    <Stack spacing={0.5}>
      {rows.map((row, i) => {
        switch (row.kind) {
          case "docTitle":
            return <DocTitle key={i}>{row.text}</DocTitle>;
          case "docBody":
            return <DocBody key={i}>{row.text}</DocBody>;
          case "comment":
            return <CommentBlock key={i}>{row.text}</CommentBlock>;
          case "code":
          default:
            return <CodeBlock key={i}>{row.text}</CodeBlock>;
        }
      })}
    </Stack>
  );
}
